import { useCallback } from 'react';
import type { useGetAvailableNetworksForAssetFragment$key } from '@onramp/data/__generated__/useGetAvailableNetworksForAssetFragment.graphql';
import type { DestinationAddressAppParam } from '@onramp/shared/appParams.schema';
import { BASE_NETWORK_NAME, ETH_ASSET_SYMBOL, USDC_ASSET_SYMBOL } from '@onramp/utils/consts';

import { useGetAvailableNetworksForAsset } from './useGetAvailableNetworksForAsset';

export type UseGetDefaultNetworkForAssetParams = {
  defaultNetworkParam: string | undefined;
  destinationWallets: DestinationAddressAppParam[];
  getAvailableNetworksQueryRef: useGetAvailableNetworksForAssetFragment$key;
};

/**
 * Gets the default network for an asset based on destination wallets,
 * the defaultNetwork param, networks available for the asset from the server,
 * prefering Base for USDC & ETH, then preferring an asset's default network,
 * then falling back to the first available network.
 */
export function useGetDefaultNetworkForAsset({
  defaultNetworkParam,
  destinationWallets,
  getAvailableNetworksQueryRef,
}: UseGetDefaultNetworkForAssetParams) {
  // 1. Filter asset's networks from server by destination wallets
  // 2. Match defaultNetwork param
  // 3. If asset is USDC or ETH & Base specified as supported, match Base
  // 4. Match asset's default network
  // 5. Match first available network

  const getAvailableNetworksForAsset = useGetAvailableNetworksForAsset({
    queryRef: getAvailableNetworksQueryRef,
    destinationWallets,
  });

  return useCallback(
    (asset: { symbol: string; uuid: string }) => {
      // 1. Filter asset's networks from server by destination wallets
      const availableNetworksForAsset = getAvailableNetworksForAsset(asset.uuid);

      // 2. Match defaultNetwork param
      const defaultNetworkFromParam = availableNetworksForAsset.find(
        ({ name }) => name === defaultNetworkParam,
      );
      if (defaultNetworkFromParam !== undefined) {
        return defaultNetworkFromParam;
      }

      // 3. If asset is USDC or Ethereum & base specified as supported, match Base
      if (asset.symbol === ETH_ASSET_SYMBOL || asset.symbol === USDC_ASSET_SYMBOL) {
        const baseNetwork = availableNetworksForAsset.find(
          ({ name }) => name === BASE_NETWORK_NAME,
        );
        if (baseNetwork !== undefined) {
          return baseNetwork;
        }
      }

      // 4. Match asset's default network
      const assetDefaultNetwork = availableNetworksForAsset.find(({ isDefault }) => isDefault);
      if (assetDefaultNetwork !== undefined) {
        return assetDefaultNetwork;
      }

      // 5. Match first available network
      return availableNetworksForAsset[0];
    },
    [getAvailableNetworksForAsset, defaultNetworkParam],
  );
}
